<template>
  <DispatchTrackerModal
    :show="show"
    :size="'lg'"
    @close="$emit('close')"
    contentStyle="max-height:90vh"
  >
    <template slot="header">
      <h4 class="modal-title">{{ $t("track_item") }}</h4>
    </template>
    <template slot="body">
      <div class="w-100">
        <div class="row mx-0">
          <div class="col-12 col-md-7">
            <div class="row">
              <div class="col-gap-2 mx-0 col-12 col-md-6">
                <div class="form-group">
                  <label for="vehicle"> {{ $t("select-device") }} </label>
                  <select
                    v-model="dataLoggerSettings"
                    name="device"
                    class="form-control"
                    required
                    id="device"
                  >
                    <option value="" disabled>{{ $t("select-device") }}</option>
                    <option
                      v-for="dataLogger in dataLoggerDetailsArray"
                      :key="dataLogger.id"
                      :value="dataLogger"
                    >
                      {{ dataLogger.settings.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="row col-gap-2 mx-0 col-12 col-md-6">
                <div>
                  <div class="d-flex col-gap-1 align-items-center mb-2">
                    <span class="gray4 title-width">{{ $t("deviceTag") }}</span>
                    <span>{{ dataLoggerSettings.deviceTag }}</span>
                  </div>
                  <div class="d-flex col-gap-1 align-items-center mb-2">
                    <span class="gray4 title-width">{{ $t("imei") }}</span>
                    <span>{{ dataLoggerSettings.imei }}</span>
                  </div>
                  <div class="d-flex col-gap-1 align-items-center">
                    <span class="gray4 title-width">{{ $t("items") }}</span>
                    <span>{{ itemsList }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-12 col-md-5">
            <DispatchStepper :step="step" :labels="labels" />
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-7">
            <div class="card">
              <div class="card-body">
                <div
                  class="d-flex justify-content-space-around col-md-10 col-lg-6 mx-auto"
                >
                  <div class="btn-container">
                    <button
                      @click="
                        tempGraph = true;
                        humiGraph = false;
                      "
                      :disabled="tempGraph == true"
                      id="graphselect"
                    >
                      {{ $t("temp") }}
                    </button>
                  </div>
                  <div class="btn-container">
                    <button
                      @click="
                        tempGraph = false;
                        humiGraph = true;
                      "
                      :disabled="tempGraph == false"
                      id="graphselect"
                    >
                      {{ $t("humid") }}
                    </button>
                  </div>
                </div>
                <div>
                  <CombinedChart
                    :tempGraph="tempGraph"
                    :humiGraph="humiGraph"
                    :stats="trackHistoryChart"
                    :hasTemp2="hasTemp2"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-5">
            <div
              class="card"
              ref="mapContent"
              id="mapCont"
              style="height: 100%; min-height: 500px"
            >
              <div class="card-body" id="map-image">
                <DeviceRoutes2 :mapPoints="trackHistoryMapPoints" />
              </div>
            </div>
          </div>
        </div>

        <div class="card-body" style="max-height: 35rem; overflow-y: auto">
          <CombinedTable
            :stats="trackingHistory"
            :type="dataLoggerSettings.type.toLowerCase()"
            :paginationMeta="trackHistoryMeta"
            :hasTemp2="hasTemp2"
            @pageChange="
              (params) =>
                $emit('pageChange', {
                  ...params,
                  id: $route.params.id,
                  trackingCode: trackingCode,
                  dataLogger: dataLoggerSettings.id,
                })
            "
            :showSearch="false"
            v-if="dataLoggerSettings.type && trackingHistory.length > 0"
          />
        </div>
      </div>
    </template>
  </DispatchTrackerModal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DispatchTrackerModal from "../../components/ModalContainer.vue";
import CombinedChart from "../../components/Devices/CombinedChart.vue";
import DeviceRoutes2 from "../../components/Devices/DeviceRoutes2.vue";
import CombinedTable from "../../components/Devices/CombinedTable.vue";
import DispatchStepper from "../../components/DispatchStepper.vue";
export default {
  name: "DispatchTracker",
  components: {
    DispatchTrackerModal,
    CombinedChart,
    DeviceRoutes2,
    CombinedTable,
    DispatchStepper,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    trackingCode: String,
  },
  data() {
    return {
      tempGraph: true,
      humiGraph: false,
      dataLoggerSettings: {},
      labels: ["Order picked up", "Order on the way", "Order arrived"],
      step: null,
    };
  },
  computed: {
    ...mapGetters([
      "trackingDetails",
      "trackingHistory",
      "trackHistoryMeta",
      "trackHistoryChart",
      "trackHistoryMapPoints",
    ]),
    hasTemp2() {
      if (typeof this.dataLoggerSettings?.hasTemp2 === "boolean") {
        return this.dataLoggerSettings.hasTemp2;
      }
      if (Array.isArray(this.trackHistoryChart)) {
        const chartHasTemp2 = this.trackHistoryChart.some(
          (reading) => reading && reading.temperature2 != null,
        );
        if (chartHasTemp2) {
          return true;
        }
      }
      if (Array.isArray(this.trackingHistory)) {
        return this.trackingHistory.some(
          (reading) => reading && reading.temperature2 != null,
        );
      }
      return false;
    },
    dataLoggerDetailsArray() {
      const uniqueDataLoggerDetails = new Map();

      this.currentDropoff?.items?.forEach((item) => {
        const { id } = item.dataLoggerDetails;
        if (!uniqueDataLoggerDetails.has(id)) {
          uniqueDataLoggerDetails.set(id, item.dataLoggerDetails);
        }
      });

      return Array.from(uniqueDataLoggerDetails.values());
    },
    itemsList() {
      return this.currentDropoff?.items?.map((item) => item.name).join(", ");
    },
    currentDropoff() {
      return this.trackingDetails?.dropoffs.find(
        (dropoff) => dropoff.trackingCode === this.trackingCode,
      );
    },
  },
  methods: {
    ...mapActions([
      "trackOrder",
      "trackOrderHistory",
      "trackOrderHistoryChart",
      "trackOrderHistoryMapPoints",
    ]),
    handleTracking(trackingCode) {
      const params = {
        limit: 10,
        page: 1,
      };

      this.handleTrackOrder(trackingCode);
      this.handleTrackOrderHistory(trackingCode, params);
      this.handleTrackOrderHistoryChart(trackingCode);
      this.handleTrackHistoryMapPoints(trackingCode);
    },
    handleTrackOrder(trackingCode) {
      this.isLoading = true;
      this.trackOrder({ id: this.$route.params.id, trackingCode })
        .then(() => {
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    handleTrackOrderHistory(trackingCode, params) {
      this.isLoading = true;
      this.trackOrderHistory({
        id: this.$route.params.id,
        trackingCode,
        params,
      })
        .then(() => {
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    handleTrackOrderHistoryChart(trackingCode) {
      this.isLoading = true;
      this.trackOrderHistoryChart({ id: this.$route.params.id, trackingCode })
        .then(() => {
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    handleTrackHistoryMapPoints(trackingCode) {
      this.isLoading = true;
      this.trackOrderHistoryMapPoints({
        id: this.$route.params.id,
        trackingCode,
      })
        .then(() => {
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
  },
  watch: {
    trackingDetails: {
      handler: function (val) {
        switch (val.status) {
          case "in_progress":
            this.step = 2;
            break;
          case "completed":
            this.step = 3;
            break;
          default:
            break;
        }
      },
      immediate: true,
      deep: true,
    },
    dataLoggerSettings: {
      handler: function (val) {
        if (val.id) {
          this.$emit("deviceSelected", val);
        }
      },
      deep: true,
    },
    dataLoggerDetailsArray: {
      handler: function (val) {
        if (val.length) {
          this.dataLoggerSettings = val[0];
        }
      },
      immediate: true,
      deep: true,
    },
    mounted() {
      this.dataLoggerSettings = {};
    },
  },
};
</script>

<style scoped>
.title-width {
  width: 90px;
}

.btn-container {
  display: flex;
  justify-content: center;
  width: 50%;
}
#graphselect {
  background-color: inherit;
  border: none;
  color: #213e7a;
  padding: 7px 25px;
  border-radius: 30px;
  outline: none;
}
#graphselect:hover {
  background-color: #213e7a;
  color: white;
}
#graphselect:disabled,
#graphselect[disabled] {
  background-color: #dee2eb;
  color: #213e7a;
}
</style>
